import { useState, useCallback, useEffect } from 'react';
import type { VoiceRecognitionState } from '../types';

export const useVoiceRecognition = (language: 'en' | 'hi') => {
  const [state, setState] = useState<VoiceRecognitionState>({
    isListening: false,
    error: null,
  });
  const [transcript, setTranscript] = useState('');

  let recognition: SpeechRecognition | null = null;

  const initializeRecognition = useCallback(() => {
    if (!('webkitSpeechRecognition' in window)) {
      setState(prev => ({ ...prev, error: 'Speech recognition is not supported in this browser.' }));
      return null;
    }

    const newRecognition = new webkitSpeechRecognition();
    newRecognition.continuous = false;
    newRecognition.interimResults = false;
    newRecognition.lang = language === 'en' ? 'en-US' : 'hi-IN';

    newRecognition.onstart = () => {
      setState(prev => ({ ...prev, isListening: true, error: null }));
    };

    newRecognition.onerror = (event) => {
      setState(prev => ({
        ...prev,
        isListening: false,
        error: `Error occurred in recognition: ${event.error}`
      }));
    };

    newRecognition.onend = () => {
      setState(prev => ({ ...prev, isListening: false }));
    };

    newRecognition.onresult = (event) => {
      const last = event.results.length - 1;
      const result = event.results[last][0].transcript;
      setTranscript(result);
    };

    return newRecognition;
  }, [language]);

  const startListening = useCallback(() => {
    if (!recognition) {
      recognition = initializeRecognition();
    }
    if (recognition) {
      recognition.start();
    }
  }, [initializeRecognition]);

  const stopListening = useCallback(() => {
    if (recognition) {
      recognition.stop();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, []);

  return {
    ...state,
    transcript,
    startListening,
    stopListening,
    resetTranscript: () => setTranscript(''),
  };
};